'use client';

import React from 'react';
import {
  CalendarIcon,
  ClockIcon,
  CheckCircledIcon,
  CrossCircledIcon,
  InfoCircledIcon,
  ExclamationTriangleIcon,
  UpdateIcon,
  // ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  // DotsHorizontalIcon,
} from '@radix-ui/react-icons';

import { Box, BoxProps } from '../box';
import { Stack } from '../stack';
import { Text } from '../text';
import { Badge } from '../badge';
// import { Button } from '../button';
import { IconButton } from '../icon-button';
import { Heading } from '../heading';
import { Spinner } from '../spinner';
import { EmptyState } from '../empty-state';
import { formatDate, formatDistanceToNow } from '../../utils/date';

import { Timeline } from './timeline';
import { TimelineItem } from './timeline-item';

export interface TimelineEvent {
  id: string;
  title: string;
  description?: string;
  type: 'milestone' | 'task' | 'event' | 'alert' | 'system';
  status?: 'completed' | 'active' | 'pending' | 'cancelled' | 'error';
  priority?: 'high' | 'medium' | 'low';
  startTime: Date | string;
  endTime?: Date | string;
  duration?: string;
  location?: string;
  participants?: Array<{
    id: string;
    name: string;
    avatar?: string;
    role?: string;
  }>;
  tags?: string[];
  metadata?: Record<string, unknown>;
  children?: TimelineEvent[];
}

export interface EventTimelineProps extends BoxProps {
  /** Events to display */
  events: TimelineEvent[];
  /** Timeline orientation */
  orientation?: 'vertical' | 'horizontal';
  /** Timeline layout */
  layout?: 'default' | 'compact' | 'detailed';
  /** Whether to show connectors */
  showConnectors?: boolean;
  /** Whether to group events by date */
  groupByDate?: boolean;
  /** Date format for grouping */
  dateGroupFormat?: Intl.DateTimeFormatOptions;
  /** Whether to show relative time */
  showRelativeTime?: boolean;
  /** Loading state */
  loading?: boolean;
  /** Empty state */
  emptyState?: React.ReactNode;
  /** Event click handler */
  onEventClick?: (event: TimelineEvent) => void;
  /** Whether events are collapsible */
  collapsible?: boolean;
  /** Initially collapsed events */
  defaultCollapsed?: string[];
  /** Locale for date formatting */
  locale?: string;
}

/**
 * EventTimeline component - specialized timeline for displaying events
 * Supports various event types, statuses, and hierarchical events
 */
export const EventTimeline: React.FC<EventTimelineProps> = ({
  events,
  orientation = 'vertical',
  layout = 'default',
  showConnectors = true,
  groupByDate = false,
  dateGroupFormat = { year: 'numeric', month: 'long', day: 'numeric' },
  showRelativeTime = true,
  loading = false,
  emptyState,
  onEventClick,
  collapsible = true,
  defaultCollapsed = [],
  locale = 'en-US',
  ...boxProps
}) => {
  const [collapsedEvents, setCollapsedEvents] = React.useState<Set<string>>(
    new Set(defaultCollapsed)
  );

  // Get icon for event type
  const getEventIcon = (event: TimelineEvent) => {
    if (event.status === 'completed') return <CheckCircledIcon />;
    if (event.status === 'cancelled') return <CrossCircledIcon />;
    if (event.status === 'error') return <ExclamationTriangleIcon />;

    switch (event.type) {
      case 'milestone':
        return <CalendarIcon />;
      case 'task':
        return <UpdateIcon />;
      case 'alert':
        return <ExclamationTriangleIcon />;
      default:
        return <InfoCircledIcon />;
    }
  };

  // Get color for event
  const getEventColor = (event: TimelineEvent) => {
    if (event.status === 'completed') return 'green.500';
    if (event.status === 'cancelled') return 'gray.500';
    if (event.status === 'error') return 'red.500';
    if (event.status === 'active') return 'blue.500';

    if (event.priority === 'high') return 'red.500';
    if (event.priority === 'low') return 'gray.500';

    switch (event.type) {
      case 'milestone':
        return 'purple.500';
      case 'alert':
        return 'orange.500';
      default:
        return 'blue.500';
    }
  };

  // Toggle event collapse
  const toggleCollapse = (eventId: string) => {
    const newCollapsed = new Set(collapsedEvents);
    if (newCollapsed.has(eventId)) {
      newCollapsed.delete(eventId);
    } else {
      newCollapsed.add(eventId);
    }
    setCollapsedEvents(newCollapsed);
  };

  // Group events by date
  const groupedEvents = React.useMemo(() => {
    if (!groupByDate) return { ungrouped: events };

    const groups: Record<string, TimelineEvent[]> = {};
    events.forEach((event) => {
      const date = new Date(event.startTime);
      const key = formatDate(date, locale, dateGroupFormat);
      if (!groups[key]) groups[key] = [];
      groups[key].push(event);
    });
    return groups;
  }, [events, groupByDate, locale, dateGroupFormat]);

  // Render event content based on layout
  const renderEventContent = (event: TimelineEvent) => {
    const hasChildren = event.children && event.children.length > 0;
    const isCollapsed = collapsedEvents.has(event.id);

    if (layout === 'compact') {
      return (
        <Box cursor={onEventClick ? 'pointer' : 'default'} onClick={() => onEventClick?.(event)}>
          <Text fontWeight="medium">{event.title}</Text>
          {showRelativeTime && (
            <Text fontSize="xs" color="fg.muted">
              {formatDistanceToNow(new Date(event.startTime), { locale })}
            </Text>
          )}
        </Box>
      );
    }

    return (
      <Box>
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
          cursor={onEventClick ? 'pointer' : 'default'}
          onClick={() => onEventClick?.(event)}
        >
          <Box flex={1}>
            <Text fontWeight="semibold" fontSize="lg" marginBottom={1}>
              {event.title}
            </Text>

            <Stack direction="row" gap={2} marginBottom={2}>
              {event.priority && (
                <Badge size="sm">
                  {event.priority === 'high'
                    ? 'Yüksek'
                    : event.priority === 'low'
                      ? 'Düşük'
                      : 'Orta'}{' '}
                  Öncelik
                </Badge>
              )}

              {event.duration && (
                <Badge size="sm">
                  <ClockIcon />
                  <Text as="span" marginLeft={1}>
                    {event.duration}
                  </Text>
                </Badge>
              )}

              {event.location && <Badge size="sm">{event.location}</Badge>}
            </Stack>

            {event.description && !isCollapsed && (
              <Text color="fg.muted" marginBottom={2}>
                {event.description}
              </Text>
            )}

            {/* Time details */}
            <Stack gap={1} fontSize="sm" color="fg.muted" marginBottom={2}>
              <Text>
                Başlangıç:{' '}
                {formatDate(new Date(event.startTime), locale, {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })}
                {showRelativeTime && (
                  <Text as="span" fontSize="xs">
                    {' '}
                    ({formatDistanceToNow(new Date(event.startTime), { locale })})
                  </Text>
                )}
              </Text>
              {event.endTime && (
                <Text>
                  Bitiş:{' '}
                  {formatDate(new Date(event.endTime), locale, {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                  })}
                </Text>
              )}
            </Stack>

            {/* Participants */}
            {event.participants && event.participants.length > 0 && !isCollapsed && (
              <Box marginBottom={2}>
                <Text fontSize="sm" fontWeight="medium" marginBottom={1}>
                  Katılımcılar ({event.participants.length})
                </Text>
                <Box display="flex" gap={2} flexWrap="wrap">
                  {event.participants.slice(0, 3).map((participant) => (
                    <Badge key={participant.id} size="sm">
                      {participant.name}
                      {participant.role && (
                        <Text as="span" color="fg.muted">
                          {' '}
                          • {participant.role}
                        </Text>
                      )}
                    </Badge>
                  ))}
                  {event.participants.length > 3 && (
                    <Badge size="sm">+{event.participants.length - 3} diğer</Badge>
                  )}
                </Box>
              </Box>
            )}

            {/* Tags */}
            {event.tags && event.tags.length > 0 && (
              <Box display="flex" gap={1} flexWrap="wrap">
                {event.tags.map((tag, index) => (
                  <Badge key={index} size="sm">
                    {tag}
                  </Badge>
                ))}
              </Box>
            )}
          </Box>

          {/* Collapse button */}
          {hasChildren && collapsible && (
            <IconButton
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                toggleCollapse(event.id);
              }}
              aria-label={isCollapsed ? 'Genişlet' : 'Daralt'}
              icon={isCollapsed ? <ChevronRightIcon /> : <ChevronDownIcon />}
            />
          )}
        </Box>

        {/* Child events */}
        {hasChildren && !isCollapsed && (
          <Box
            marginTop={3}
            marginLeft={4}
            paddingLeft={4}
            borderLeft="2px solid"
            borderColor="border.muted"
          >
            <EventTimeline
              events={event.children || []}
              orientation={orientation}
              layout={layout}
              showConnectors={showConnectors}
              groupByDate={false}
              showRelativeTime={showRelativeTime}
              onEventClick={onEventClick}
              collapsible={collapsible}
              locale={locale}
            />
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" alignItems="center" justifyContent="center" padding={8} {...boxProps}>
        <Spinner size="lg" />
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Box {...boxProps}>
        {emptyState || (
          <EmptyState
            title="Etkinlik bulunamadı"
            description="Henüz görüntülenecek etkinlik yok."
            icon={<CalendarIcon />}
          />
        )}
      </Box>
    );
  }

  return (
    <Box {...boxProps}>
      {Object.entries(groupedEvents).map(([dateKey, dateEvents]) => (
        <Box key={dateKey} marginBottom={6}>
          {groupByDate && dateKey !== 'ungrouped' && (
            <Heading size="sm" marginBottom={4} color="fg.muted">
              {dateKey}
            </Heading>
          )}

          <Timeline
            orientation={orientation}
            showConnectors={showConnectors}
            spacing={layout === 'compact' ? 3 : 6}
          >
            {dateEvents.map((event) => (
              <TimelineItem
                key={event.id}
                title=""
                icon={getEventIcon(event)}
                markerColor={getEventColor(event)}
                status={event.status === 'cancelled' ? 'error' : event.status}
                additionalContent={renderEventContent(event)}
              />
            ))}
          </Timeline>
        </Box>
      ))}
    </Box>
  );
};

export default EventTimeline;
